import type { ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "../../../../context/ThemeContext";
import { buildThemeClasses } from "../../theme";
import type { Allocation, BucketAmounts, Cohort, EconomicsReport, FunnelStage, PipelineRow } from "../../services/economicsService";
import { BUCKET_META, BUCKET_ORDER, OUTCOME_META, PALETTE, days, money, monthLabel, num, pct } from "./economicsFormat";

// ============================================================================
// Exhibits del tablero de economía del embudo (#1291, v2 #1352).
//
// Reglas que siguen todos: un solo eje por gráfica; el color sigue a la entidad
// (cada bolsa de costo y cada resultado tienen siempre el mismo); marcas
// delgadas; leyenda cuando hay dos o más series; el texto va en tinta, no en el
// color de la serie; y todo lo que se grafica también está en una tabla.
// ============================================================================

const kMoney = (v: unknown) => money(Number(v), { compact: true });

/** Orden fijo de las series en las leyendas (recharts las ordena alfabéticamente si no). */
const LEGEND_ORDER: string[] = [...BUCKET_ORDER, "activos", "pausa", "perdidos", "lead", "demo"];

function useChartTheme() {
  const { isDarkMode } = useTheme();
  const ink = isDarkMode ? "#e5e7eb" : "#374151";
  return {
    /** Props comunes de la leyenda: en el orden de las series y con el texto en tinta. */
    legend: {
      wrapperStyle: { fontSize: 12 },
      itemSorter: (item: { dataKey?: unknown }) => LEGEND_ORDER.indexOf(String(item.dataKey)),
      formatter: (value: unknown) => <span style={{ color: ink }}>{String(value)}</span>,
    },
    isDarkMode,
    tc: buildThemeClasses(isDarkMode),
    grid: isDarkMode ? PALETTE.grid.dark : PALETTE.grid.light,
    tick: { fontSize: 11, fill: isDarkMode ? PALETTE.tick.dark : PALETTE.tick.light },
    funnel: isDarkMode ? "#7aa2d6" : PALETTE.funnel,
    tooltip: {
      background: isDarkMode ? "#1f1f1f" : "#fff",
      border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
      borderRadius: 8,
      fontSize: 12,
      color: isDarkMode ? "#f3f4f6" : "#111827",
    },
  };
}

/**
 * Un exhibit de informe: número y tema arriba, el TÍTULO es la conclusión, y
 * la fuente y las notas van al pie.
 */
export function Exhibit({ n, kicker, title, children, source, aside, className = "", testId }: { n?: number; kicker: string; title: string; children: ReactNode; source?: string; aside?: ReactNode; className?: string; testId?: string }) {
  const { tc } = useChartTheme();
  return (
    <section className={`rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg} ${className}`} data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={`text-[11px] font-semibold uppercase tracking-wider ${tc.textSecondary}`}>
            {n != null ? `Exhibit ${n} · ` : ""}
            {kicker}
          </p>
          <h3 className={`mt-0.5 text-base font-bold leading-snug ${tc.text}`}>{title}</h3>
        </div>
        {aside}
      </div>
      {children}
      {source && <p className={`mt-3 border-t pt-2 text-[11px] leading-relaxed ${tc.border} ${tc.textSecondary}`}>{source}</p>}
    </section>
  );
}

export function Empty({ text }: { text: string }) {
  const { tc } = useChartTheme();
  return <p className={`py-8 text-center text-sm ${tc.textSecondary}`}>{text}</p>;
}

/** Leyenda de las bolsas de costo (una sola vez por sección). */
export function BucketLegend({ amounts, total }: { amounts?: BucketAmounts; total?: number }) {
  const { tc } = useChartTheme();
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs" aria-label="Bolsas de costo">
      {BUCKET_ORDER.filter((id) => !amounts || amounts[id] > 0 || id !== "otherMarketing").map((id) => (
        <li key={id} className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: BUCKET_META[id].color }} />
          <span className={tc.text}>{BUCKET_META[id].label}</span>
          {amounts && total ? <span className={`tabular-nums ${tc.textSecondary}`}>{pct(amounts[id] / total)}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/** Barra 100 % con el reparto por bolsa: acompaña a cada total del tablero. */
export function BucketSplitBar({ amounts, height = 8, label }: { amounts: BucketAmounts | null | undefined; height?: number; label: string }) {
  const { isDarkMode } = useChartTheme();
  const total = amounts ? BUCKET_ORDER.reduce((a, id) => a + (amounts[id] || 0), 0) : 0;
  if (!amounts || !(total > 0)) return <div className={`w-full rounded-full ${isDarkMode ? "bg-white/10" : "bg-gray-100"}`} style={{ height }} aria-hidden />;
  const text = BUCKET_ORDER.filter((id) => amounts[id] > 0)
    .map((id) => `${BUCKET_META[id].short} ${money(amounts[id])} (${pct(amounts[id] / total)})`)
    .join(" · ");
  return (
    <div className="flex w-full gap-0.5 overflow-hidden rounded-full" style={{ height }} role="img" aria-label={`${label}: ${text}`} title={text}>
      {BUCKET_ORDER.filter((id) => amounts[id] > 0).map((id) => (
        <div key={id} style={{ width: `${(amounts[id] / total) * 100}%`, background: BUCKET_META[id].color }} />
      ))}
    </div>
  );
}

/** Inversión mensual apilada por bolsa. */
export function MonthlySpendChart({ monthly }: { monthly: EconomicsReport["costs"]["monthly"] }) {
  const t = useChartTheme();
  if (!monthly.some((m) => m.total > 0)) return <Empty text="Sin inversión comercial registrada en el periodo." />;
  const data = monthly.map((m) => ({ ...m, label: monthLabel(m.period) }));
  const used = BUCKET_ORDER.filter((id) => monthly.some((m) => m[id] > 0));
  return (
    <div style={{ width: "100%", height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8, bottom: 4 }}>
          <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={t.tick} />
          <YAxis tick={t.tick} tickFormatter={kMoney} width={60} />
          <Tooltip contentStyle={t.tooltip} formatter={(value, name) => [money(Number(value)), String(name)]} />
          <Legend {...t.legend} />
          {used.map((id, i) => (
            <Bar key={id} name={BUCKET_META[id].label} dataKey={id} stackId="cost" fill={BUCKET_META[id].color} stroke={t.isDarkMode ? "#1f1f1f" : "#fff"} strokeWidth={1} radius={i === used.length - 1 ? [4, 4, 0, 0] : undefined} isAnimationActive={false} maxBarSize={44} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Embudo: cuántos llegaron a cada etapa. */
export function FunnelChart({ stages }: { stages: FunnelStage[] }) {
  const t = useChartTheme();
  if (!stages.some((r) => r.reached > 0)) return <Empty text="Sin leads ni negocios en el periodo." />;
  const fill = (k: FunnelStage["kind"]) => (k === "won" ? OUTCOME_META.won.color : k === "open" ? t.funnel : PALETTE.funnelLight);
  return (
    <div style={{ width: "100%", height: Math.max(240, stages.length * 40) }}>
      <ResponsiveContainer>
        <BarChart data={stages} layout="vertical" margin={{ left: 8, right: 64, top: 4, bottom: 4 }} barCategoryGap={6}>
          <CartesianGrid horizontal={false} stroke={t.grid} strokeDasharray="3 3" />
          <XAxis type="number" tick={t.tick} allowDecimals={false} />
          <YAxis type="category" dataKey="label" width={150} tick={t.tick} />
          <Tooltip
            contentStyle={t.tooltip}
            formatter={(value, _name, item) => {
              const s = item.payload as FunnelStage;
              return [`${num(Number(value))} · ${money(s.costPerUnit)} por unidad${s.conversionFromPrev != null ? ` · ${pct(s.conversionFromPrev)} del paso anterior` : ""}`, s.label];
            }}
          />
          <Bar dataKey="reached" radius={[0, 4, 4, 0]} isAnimationActive={false}>
            {stages.map((r) => (
              <Cell key={r.id} fill={fill(r.kind)} />
            ))}
            <LabelList dataKey="reached" position="right" formatter={(v: unknown) => num(Number(v))} style={{ fontSize: 11, fill: t.tick.fill }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Dónde están hoy los negocios que llegaron hasta cada etapa: activos, en pausa, perdidos. */
export function StageOutcomeChart({ stages }: { stages: FunnelStage[] }) {
  const t = useChartTheme();
  const rows = stages.filter((s) => s.kind === "open").map((s) => ({ label: s.label, activos: s.current ?? 0, pausa: s.parkedHere, perdidos: s.lostHere }));
  if (!rows.some((r) => r.activos || r.pausa || r.perdidos)) return <Empty text="Sin negocios en etapas abiertas." />;
  return (
    <div style={{ width: "100%", height: Math.max(220, rows.length * 42) }}>
      <ResponsiveContainer>
        <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }} barCategoryGap={8}>
          <CartesianGrid horizontal={false} stroke={t.grid} strokeDasharray="3 3" />
          <XAxis type="number" tick={t.tick} allowDecimals={false} />
          <YAxis type="category" dataKey="label" width={150} tick={t.tick} />
          <Tooltip contentStyle={t.tooltip} />
          <Legend {...t.legend} />
          <Bar name="Siguen activos aquí" dataKey="activos" stackId="o" fill={OUTCOME_META.open.color} stroke={t.isDarkMode ? "#1f1f1f" : "#fff"} strokeWidth={1} isAnimationActive={false} maxBarSize={22} />
          <Bar name="Quedaron en pausa" dataKey="pausa" stackId="o" fill={OUTCOME_META.parked.color} stroke={t.isDarkMode ? "#1f1f1f" : "#fff"} strokeWidth={1} isAnimationActive={false} maxBarSize={22} />
          <Bar name="Se perdieron aquí" dataKey="perdidos" stackId="o" fill={OUTCOME_META.lost.color} stroke={t.isDarkMode ? "#1f1f1f" : "#fff"} strokeWidth={1} radius={[0, 4, 4, 0]} isAnimationActive={false} maxBarSize={22} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** A dónde fue la inversión: una barra 100 % por resultado del negocio. */
export function AllocationBar({ allocation }: { allocation: Allocation[] }) {
  const { tc, isDarkMode } = useChartTheme();
  const rows = allocation.filter((a) => a.deals > 0);
  if (!rows.length) return <Empty text="Sin negocios ni inversión que repartir." />;
  return (
    <div>
      <div className="flex h-9 w-full gap-0.5 overflow-hidden rounded-lg" role="img" aria-label={rows.map((a) => `${a.label} ${pct(a.share)}`).join(", ")}>
        {rows.map((a) => (
          <div key={a.kind} className="flex items-center justify-center text-[11px] font-semibold text-white" style={{ width: `${a.share * 100}%`, background: OUTCOME_META[a.kind].color }} title={`${a.label}: ${money(a.cost)} (${pct(a.share)})`}>
            {a.share >= 0.08 ? pct(a.share) : ""}
          </div>
        ))}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-2 text-xs lg:grid-cols-4">
        {allocation.map((a) => (
          <li key={a.kind} className={`rounded-lg border p-2 ${tc.border} ${isDarkMode ? "" : "bg-white"}`}>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: OUTCOME_META[a.kind].color }} />
              <span className={tc.text}>{a.label}</span>
            </span>
            <p className={`mt-1 text-sm font-bold tabular-nums ${tc.text}`}>{money(a.cost)}</p>
            <p className={`tabular-nums ${tc.textSecondary}`}>
              {num(a.deals)} negocios · {pct(a.share)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Demos (reuniones) por mes. Una sola serie: las notas de seguimiento son otra
 * escala y viven en la tabla de cohortes, no en un segundo eje.
 */
export function ActivityChart({ monthly }: { monthly: EconomicsReport["activity"]["monthly"] }) {
  const t = useChartTheme();
  if (!monthly.some((m) => m.demos)) return <Empty text="Sin demos registradas en el periodo." />;
  const data = monthly.map((m) => ({ label: monthLabel(m.period), demos: m.demos, notas: m.notes }));
  return (
    <div style={{ width: "100%", height: 240 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: 8, right: 8, top: 16, bottom: 4 }}>
          <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={t.tick} />
          <YAxis tick={t.tick} allowDecimals={false} width={40} />
          <Tooltip contentStyle={t.tooltip} formatter={(value, _n, item) => [`${num(Number(value))} demos · ${num((item.payload as { notas: number }).notas)} notas de seguimiento`, "Actividad"]} />
          <Bar name="Demos (reuniones)" dataKey="demos" fill={t.funnel} radius={[4, 4, 0, 0]} isAnimationActive={false} maxBarSize={30}>
            <LabelList dataKey="demos" position="top" formatter={(v: unknown) => (Number(v) > 0 ? num(Number(v)) : "")} style={{ fontSize: 11, fill: t.tick.fill }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Costo por lead y por demo, mes a mes (misma unidad: MXN). */
export function UnitCostTrend({ cohorts }: { cohorts: Cohort[] }) {
  const t = useChartTheme();
  const data = cohorts.map((c) => ({ label: monthLabel(c.period), lead: c.costPerLead, demo: c.costPerDemo }));
  if (!data.some((d) => d.lead != null || d.demo != null)) return <Empty text="Sin meses con costo y leads o demos a la vez." />;
  return (
    <div style={{ width: "100%", height: 240 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ left: 8, right: 16, top: 8, bottom: 4 }}>
          <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={t.tick} />
          <YAxis tick={t.tick} tickFormatter={kMoney} width={60} />
          <Tooltip contentStyle={t.tooltip} formatter={(value, name) => [money(Number(value)), String(name)]} />
          <Legend {...t.legend} />
          <Line name="Costo por lead" dataKey="lead" stroke={t.funnel} strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} connectNulls />
          <Line name="Costo por demo" dataKey="demo" stroke={PALETTE.accent} strokeWidth={2} dot={{ r: 4 }} strokeDasharray="5 3" isAnimationActive={false} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Mediana de días por etapa. */
export function StageDurationChart({ stages }: { stages: FunnelStage[] }) {
  const t = useChartTheme();
  const rows = stages.filter((d) => d.kind === "open" && d.medianDays != null);
  if (!rows.length) return <Empty text="Sin recorrido de etapas para medir tiempos." />;
  return (
    <div style={{ width: "100%", height: Math.max(200, rows.length * 40) }}>
      <ResponsiveContainer>
        <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 56, top: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke={t.grid} strokeDasharray="3 3" />
          <XAxis type="number" tick={t.tick} unit=" d" />
          <YAxis type="category" dataKey="label" width={150} tick={t.tick} />
          <Tooltip contentStyle={t.tooltip} formatter={(value, _n, item) => [`${days(Number(value))} de mediana · ${num((item.payload as FunnelStage).durationSamples)} negocios`, "Tiempo en la etapa"]} />
          <Bar dataKey="medianDays" fill={PALETTE.accent} radius={[0, 4, 4, 0]} isAnimationActive={false} maxBarSize={20}>
            <LabelList dataKey="medianDays" position="right" formatter={(v: unknown) => days(Number(v))} style={{ fontSize: 11, fill: t.tick.fill }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Mini barra 100 % del estado de un pipeline. */
export function PipelineMixBar({ row }: { row: PipelineRow }) {
  const parts = [
    { kind: "won" as const, n: row.won },
    { kind: "open" as const, n: row.active },
    { kind: "parked" as const, n: row.parked },
    { kind: "lost" as const, n: row.lost },
  ].filter((p) => p.n > 0);
  if (!row.deals) return null;
  return (
    <div className="flex h-2.5 w-full min-w-[120px] gap-0.5 overflow-hidden rounded-full" role="img" aria-label={parts.map((p) => `${OUTCOME_META[p.kind].label} ${p.n}`).join(", ")}>
      {parts.map((p) => (
        <div key={p.kind} style={{ width: `${(p.n / row.deals) * 100}%`, background: OUTCOME_META[p.kind].color }} title={`${OUTCOME_META[p.kind].label}: ${p.n}`} />
      ))}
    </div>
  );
}
